import React from 'react';

import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';
import Spinner from '~/components/Common/Spinner';
import RatePlanBonusInclusions from '~/components/Vendors/RatePlans/RatePlanBonusInclusions';
import RatePlanOffersContainer from '~/components/Vendors/RatePlans/RatePlanOffersContainer';

import { useGetRatePlan } from '~/hooks/apis/reservation/useGetRatePlan';

import { getCancellationPolicyLabel } from '~/utils/cancellationPolicy';
import { humanize } from '~/utils/humanize';

import RoomTypeBreadcrumbs from './RoomTypeBreadcrumbs';

interface Props {
  vendorId: string;
  roomTypeName: string;
  ratePlanId: string;
  onBack: () => void;
  onNavigateToRoomTypes: () => void;
}

export default function RatePlanDetailPanel({
  vendorId,
  roomTypeName,
  ratePlanId,
  onBack,
  onNavigateToRoomTypes,
}: Props) {
  const { data: ratePlan, isLoading, error } = useGetRatePlan(ratePlanId);

  if (error) {
    return <Alert severity="error">Failed to load rate plan: {error.message}</Alert>;
  }

  if (isLoading || !ratePlan) {
    return <Spinner />;
  }

  return (
    <Box>
      <RoomTypeBreadcrumbs
        roomTypeName={roomTypeName}
        currentPage={ratePlan.name}
        onNavigateToRoomTypes={onNavigateToRoomTypes}
        onNavigateToRoomType={onBack}
      />

      <Typography variant="h4">{ratePlan.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rate plan record · opened from {roomTypeName}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box display="grid" gap={3} gridTemplateColumns="repeat(4, 1fr)">
          <Box>
            <DefinitionTitle>Name</DefinitionTitle>
            <DefinitionText>{ratePlan.name}</DefinitionText>
          </Box>

          <Box>
            <DefinitionTitle>Rate Type</DefinitionTitle>
            <DefinitionText>{ratePlan.rate_type}</DefinitionText>
          </Box>

          {ratePlan.rate_type === 'net' ? (
            <Box>
              <DefinitionTitle>Markup</DefinitionTitle>
              <DefinitionText>{ratePlan.markup}%</DefinitionText>
            </Box>
          ) : (
            <>
              <Box>
                <DefinitionTitle>Commission</DefinitionTitle>
                <DefinitionText>{ratePlan.commission}%</DefinitionText>
              </Box>
              <Box>
                <DefinitionTitle>Discount</DefinitionTitle>
                <DefinitionText>{ratePlan.discount}%</DefinitionText>
              </Box>
            </>
          )}

          <Box>
            <DefinitionTitle>Instant Purchase or Deposit</DefinitionTitle>
            <DefinitionText>{ratePlan.type_of_pay ? humanize(ratePlan.type_of_pay) : 'No'}</DefinitionText>
          </Box>

          <Box>
            <DefinitionTitle>Product Type</DefinitionTitle>
            <DefinitionText>{humanize(ratePlan.product_type)}</DefinitionText>
          </Box>

          <Box>
            <DefinitionTitle>Cancellation Policy</DefinitionTitle>
            <DefinitionText>{getCancellationPolicyLabel(ratePlan.cancellation_policy)}</DefinitionText>
          </Box>

          <Box>
            <DefinitionTitle>Status</DefinitionTitle>
            <Chip
              label={humanize(ratePlan.status)}
              size="small"
              color={ratePlan.status === 'content-approved' ? 'success' : 'default'}
              sx={{ mt: 0.5 }}
            />
          </Box>

          <Box>
            <DefinitionTitle>Stay Pay</DefinitionTitle>
            <DefinitionText>{ratePlan.is_stay_pay_enabled ? 'Yes' : 'No'}</DefinitionText>
          </Box>

          <Box>
            <DefinitionTitle>Extranet Added Rate</DefinitionTitle>
            <DefinitionText>{ratePlan.source === 'extranet' ? 'Yes' : 'No'}</DefinitionText>
          </Box>

          {!!ratePlan.rate_plan_code && (
            <Box>
              <DefinitionTitle>Rate Plan Code</DefinitionTitle>
              <DefinitionText>{ratePlan.rate_plan_code}</DefinitionText>
            </Box>
          )}
        </Box>
      </Paper>

      {!!ratePlan.cancellation_policy_detail && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Cancellation policy detail
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {getCancellationPolicyLabel(ratePlan.cancellation_policy)}
          </Typography>
          <Stack spacing={0.5}>
            {ratePlan.cancellation_policy_detail.map((line, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {line}
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Package regions
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {ratePlan.regions?.length ? (
            ratePlan.regions.map((region) => <Chip key={region} label={region} size="small" />)
          ) : (
            <Typography color="text.secondary">No regions.</Typography>
          )}
        </Stack>
      </Paper>

      {ratePlan.bonus_inclusions?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <RatePlanBonusInclusions bonusInclusions={ratePlan.bonus_inclusions} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <RatePlanOffersContainer ratePlanId={ratePlan.id} vendorId={vendorId} />
      </Paper>
    </Box>
  );
}
